/* eslint-disable security/detect-non-literal-fs-filename */
import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { repeat, Subscription, switchMap, takeWhile } from 'rxjs';
import { ProductService } from 'src/app/core/api/product/product.service';
import { UserService } from 'src/app/core/api/user/user.service';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { DownloadReportDTO } from 'src/app/shared/models/dtos/download-report/download-report-dto';
import { DownloadReportResponseDTO } from 'src/app/shared/models/dtos/download-report/download-report-response-dto';
import { DownloadReportStatus } from 'src/app/utils/downloadReportStatus';
import { DateFilterStrategy, ReportType } from 'src/app/utils/reportType';
import { ReportDownloadData } from 'src/app/shared/models/reports/report-download';

const POLLING_INTERVAL = 5000; // 5 seconds
const START_DATE = '1970-01-01';

@Component({
  selector: 'app-download-report-item',
  templateUrl: './download-report-item.component.html',
  styleUrls: ['./download-report-item.component.scss'],
})
/**
 * Dowload report item for download report page
 */
export class DownloadReportItemComponent {
  @Input() itemData!: ReportDownloadData;
  @Output() loading = new EventEmitter<boolean>(false);
  @Input() disableButton: boolean = false;
  private readonly subsPollingReport = new Subscription();
  /**
   * Constructor
   */
  constructor(
    @Inject('Window') private window: Window,
    private readonly translationService: TranslationService,
    private readonly productService: ProductService,
    private readonly snackBarService: SnackBarService,
    private readonly userService: UserService,
    private readonly dialogService: DialogService
  ) {}

  /**
   * clean up subscriptions
   */
  ngOnDestroy() {
    this.unsubscribePollingReport();
  }

  /**
   * unsubscribe polling Report
   */
  private unsubscribePollingReport() {
    this.loading.emit(false);
    this.subsPollingReport.unsubscribe();
  }

  /**
   * Opens url on new tab
   */
  openLink() {
    this.productService.getWarrantiesURL().subscribe({
      next: (url) => {
        this.window.open(url.infoUrl, '_blank');
      },
      error: () => {
        this.translationService
          .translateString('no-warranty-url-found')
          .then((string) => this.snackBarService.error(string));
      },
    });
  }

  /**
   * Decides which route to take when downloading, modal or direct download.
   * @param {ReportDownloadData} clickedItem
   */
  prepareDownload(clickedItem: ReportDownloadData) {
    if (clickedItem.triggersModal) {
      this.openModal(clickedItem.type!);
    } else {
      const downloadData = new DownloadReportDTO(clickedItem.type!.toString());
      this.downloadFile(downloadData);
    }
  }

  /**
   * Downloads a report file
   * @param {DownloadReportDTO} downloadData
   */
  downloadFile(downloadData: DownloadReportDTO) {
    this.loading.emit(true);
    let reportStatus: DownloadReportResponseDTO;
    const subDownloadReport = this.userService
      .getReportDownload(downloadData)
      .pipe(
        switchMap((uuid: string) =>
          this.userService.getDownloadReportStatus(uuid).pipe(
            repeat({ delay: POLLING_INTERVAL }),
            takeWhile((response: DownloadReportResponseDTO) => {
              reportStatus = response;
              return response.reportStatus === DownloadReportStatus.INCOMPLETE;
            })
          )
        )
      )
      .subscribe({
        complete: () => {
          if (reportStatus?.fileName) {
            this.handleFileDownload(reportStatus.fileName);
          } else {
            this.handleError('report-download-error');
          }
        },
        error: () => this.handleError('report-download-error'),
      });
    this.subsPollingReport.add(subDownloadReport);
  }

  /**
   * Get presigned URL to download file
   * @param {string} fileName
   */
  private handleFileDownload(fileName: string) {
    this.userService.getPresignedReport(fileName).subscribe({
      next: (response) => {
        this.loading.emit(false);
        this.window.location.assign(response);
        this.showSuccessMessage('success-creating-report');
      },
      error: () => this.handleError('report-download-error'),
    });
  }

  /**
   * Handles error when downloading file
   * @param {string} translationKey
   */
  private handleError(translationKey: string) {
    this.loading.emit(false);
    this.translationService
      .translateString(translationKey)
      .then((message) => this.snackBarService.error(message));
  }

  /**
   * Shows success message when file is downloaded
   * @param {string} translationKey
   */
  private showSuccessMessage(translationKey: string) {
    this.translationService
      .translateString(translationKey)
      .then((message) => this.snackBarService.success(message));
  }

  /**
   * Opens modal to set info necessary for report file download
   * @param {ReportType} reportType
   */
  openModal(reportType: ReportType) {
    this.dialogService.openDownloadReportsModal(reportType, (data) => {
      let dateFilterStrategy =
        data.option && Object.keys(DateFilterStrategy).includes(data.option)
          ? DateFilterStrategy[data.option as keyof typeof DateFilterStrategy]
          : DateFilterStrategy.SIX_MONTHS;
      const isoStartDate = data.dateRange?.start?.toISOString();
      const isoEndDate = data.dateRange?.end?.toISOString();

      let startDate = isoStartDate?.split('T')[0];
      let endDate = isoEndDate?.split('T')[0];

      if (dateFilterStrategy === DateFilterStrategy.ALL_TIME) {
        dateFilterStrategy = DateFilterStrategy.DATE_RANGE;
        startDate = START_DATE;
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        endDate = tomorrow.toISOString().split('T')[0];
      }

      const reportData: DownloadReportDTO = new DownloadReportDTO(
        reportType.toString()!,
        startDate,
        endDate,
        dateFilterStrategy
      );
      this.downloadFile(reportData);
    });
  }
}
